import { useState } from 'react';
import { CurrencyDollar, Package } from 'shared/icons';
import api from '../../services/api';
import { FormatDateTimeBr } from '../../utils/date';
import { FormatPrice } from '../../utils/format';
import StatusBadge from '../StatusBadge';
import Button from '../ui/Button';
import StandardTable from '../ui/StandardTable';

const PromotionsByItem = () => {
    const [itemId, setItemId] = useState('');
    const [itemCampaigns, setItemCampaigns] = useState([]);
    const [itemSalePrice, setItemSalePrice] = useState(null);
    const [loadingItem, setLoadingItem] = useState(false);
    const [error, setError] = useState(null);

    const loadItemCampaigns = async (itemIdToSearch) => {
        if (!itemIdToSearch || !itemIdToSearch.trim()) {
            setItemCampaigns([]);
            setItemSalePrice(null);
            return;
        }

        try {
            setLoadingItem(true);
            setError(null);

            const [campaignsRes, salePriceRes] = await Promise.all([
                api.get(`/promotions/items/${itemIdToSearch.trim()}`),
                api.get(`/promotions/items/${itemIdToSearch.trim()}/sale-price`),
            ]);

            if (campaignsRes.success !== false) {
                setItemCampaigns(campaignsRes.campaigns || []);
            } else {
                setItemCampaigns([]);
            }

            if (salePriceRes.success !== false && salePriceRes.sale_price) {
                setItemSalePrice(salePriceRes.sale_price);
            } else {
                setItemSalePrice(null);
            }
        } catch (err) {
            setError({
                title: 'Erro ao Carregar Campanhas do Item',
                message: err.message || 'Nao foi possivel carregar as campanhas do item.',
                type: 'error',
            });
            setItemCampaigns([]);
            setItemSalePrice(null);
        } finally {
            setLoadingItem(false);
        }
    };

    const handleItemSearch = (e) => {
        e.preventDefault();
        loadItemCampaigns(itemId);
    };

    const formatCurrency = (value) => FormatPrice(value);
    const formatDate = (dateString) => FormatDateTimeBr(dateString, { blank: 'N/A' });
    const getStatusBadge = (status) => <StatusBadge domain="promotion" status={status} />;
    const getTypeBadge = (type) => <StatusBadge domain="promotionType" status={type} />;

    const columns = [
        {
            key: 'id',
            label: 'ID',
            getValue: (row) => row.id || '-',
            cellClassName: 'font-mono text-mono-900 dark:text-white',
        },
        {
            key: 'name',
            label: 'Nome',
            getValue: (row) => row.name || '-',
        },
        {
            key: 'type',
            label: 'Tipo',
            sortable: false,
            getValue: (row) => row.type || '-',
            render: (row) => getTypeBadge(row.type),
        },
        {
            key: 'status',
            label: 'Status',
            sortable: false,
            getValue: (row) => row.status || '-',
            render: (row) => getStatusBadge(row.status),
        },
        {
            key: 'original_price',
            label: 'Preco Original',
            align: 'right',
            searchable: false,
            getValue: (row) => formatCurrency(row.original_price),
        },
        {
            key: 'price',
            label: 'Preco Promocional',
            align: 'right',
            searchable: false,
            getValue: (row) => formatCurrency(row.price),
            cellClassName: 'font-semibold text-black dark:text-white',
        },
        {
            key: 'discount',
            label: 'Desconto',
            align: 'right',
            sortable: false,
            searchable: false,
            getValue: (row) => {
                if (!row.original_price || !row.price) {
                    return '-';
                }
                return `-${((1 - row.price / row.original_price) * 100).toFixed(1)}%`;
            },
        },
        {
            key: 'meli_percentage',
            label: 'ML %',
            align: 'center',
            searchable: false,
            getValue: (row) => (row.meli_percentage ? `${row.meli_percentage}%` : '-'),
        },
        {
            key: 'seller_percentage',
            label: 'Seller %',
            align: 'center',
            searchable: false,
            getValue: (row) => (row.seller_percentage ? `${row.seller_percentage}%` : '-'),
        },
        {
            key: 'start_date',
            label: 'Inicio',
            getValue: (row) => formatDate(row.start_date),
        },
        {
            key: 'finish_date',
            label: 'Fim',
            getValue: (row) => formatDate(row.finish_date),
        },
    ];

    return (
        <div className="space-y-6">
            {/* Busca por Item */}
            <div>
                <form onSubmit={handleItemSearch} className="flex gap-4">
                    <div className="flex-1 relative">
                        <Package
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-mono-400"
                            size={20}
                        />
                        <input
                            type="text"
                            placeholder="Digite o ID do item (ex: MLB1234567890)"
                            value={itemId}
                            onChange={(e) => setItemId(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-mono-300 dark:border-mono-700 rounded-lg bg-white dark:bg-mono-900 text-mono-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <Button type="submit" disabled={loadingItem || !itemId.trim()}>
                        {loadingItem ? 'Buscando...' : 'Buscar'}
                    </Button>
                </form>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
                    <p className="text-red-800 dark:text-red-200 font-medium mb-2">{error.title}</p>
                    <p className="text-red-600 dark:text-red-300 text-sm">{error.message}</p>
                </div>
            )}

            {/* Preco de Venda Atual */}
            {itemSalePrice && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                        <CurrencyDollar size={18} />
                        Preco de Venda Atual
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="text-blue-700 dark:text-blue-300">Preco Regular:</span>
                            <span className="ml-2 font-semibold text-blue-900 dark:text-blue-100">
                                {formatCurrency(itemSalePrice.regular_amount)}
                            </span>
                        </div>
                        <div>
                            <span className="text-blue-700 dark:text-blue-300">Preco Promocional:</span>
                            <span className="ml-2 font-semibold text-green-600 dark:text-green-400">
                                {formatCurrency(itemSalePrice.amount)}
                            </span>
                        </div>
                        {itemSalePrice.metadata && (
                            <>
                                <div>
                                    <span className="text-blue-700 dark:text-blue-300">Campanha Ativa:</span>
                                    <span className="ml-2 font-semibold text-blue-900 dark:text-blue-100 font-mono text-xs">
                                        {itemSalePrice.metadata.campaign_id || '-'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-blue-700 dark:text-blue-300">Tipo:</span>
                                    <span className="ml-2 font-semibold text-blue-900 dark:text-blue-100">
                                        {itemSalePrice.metadata.promotion_type || '-'}
                                    </span>
                                </div>
                                {itemSalePrice.metadata.promotion_id && (
                                    <div className="col-span-2">
                                        <span className="text-blue-700 dark:text-blue-300">ID da Oferta:</span>
                                        <span className="ml-2 font-semibold text-blue-900 dark:text-blue-100 font-mono text-xs">
                                            {itemSalePrice.metadata.promotion_id}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                        {itemSalePrice.reference_date && (
                            <div className="col-span-2 text-xs text-blue-600 dark:text-blue-400 mt-2">
                                Ultima atualizacao: {formatDate(itemSalePrice.reference_date)}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Tabela de Campanhas do Item */}
            {itemId ? (
                <StandardTable
                    rows={loadingItem || error ? [] : itemCampaigns}
                    columns={columns}
                    rowKey={(row, index) => row.id || `${row.type || 'campaign'}-${index}`}
                    countLabel="campanhas"
                    emptyMessage={
                        loadingItem
                            ? 'Carregando campanhas do item...'
                            : error
                              ? ''
                              : 'Nenhuma campanha encontrada para este item'
                    }
                    searchPlaceholder="Buscar por ID, nome ou tipo..."
                    exportFileName={`campaigns-${itemId.trim() || 'item'}`}
                />
            ) : null}

            {!itemId && (
                <div className="text-center py-12 text-mono-500 dark:text-mono-400">
                    <Package size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Digite um ID de item para buscar suas campanhas</p>
                </div>
            )}
        </div>
    );
};

export default PromotionsByItem;
